import { Link } from "react-router-dom";
import { ArrowRight, Search, FileText, Calculator, ShoppingCart, Mail, ShieldCheck, CheckCircle2 } from "lucide-react";

/**
 * Tres pilares estilo Reaktor: 01 · ESCÁNER · 02 · GENERADOR · 03 · DEEP SCAN
 * Cada pilar combina copy persuasivo a la izquierda y mockup del producto a la derecha (intercalando).
 */

export default function Pillars() {
  return (
    <section
      data-testid="pillars-section"
      className="bg-white relative"
    >
      {/* Intro */}
      <div className="px-6 py-20 sm:py-28 border-b border-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase">
            Cómo funciona
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight italic">
            Detectar lo que falta, <span className="not-italic font-display text-blue-700">redactarlo</span>, y demostrar que está hecho.
          </h2>
          <p className="mt-5 text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Tres pasos. Sin abogados, sin plantillas sueltas. Listo para mostrar a la APDP si te fiscalizan.
          </p>
        </div>
      </div>

      {/* Pillar 01 — Escáner */}
      <Pillar
        num="01"
        category="Detección"
        title="Escanea tu sitio y encuentra los hallazgos críticos."
        subtitle="El escáner gratuito analiza tu sitio en 30 segundos contra los 12 ítems del Art. 14 ter de la Ley 21.719: HTTPS, política, banner de cookies, derechos ARCO, trackers de terceros, formularios sin consentimiento, decisiones automatizadas y más."
        bullets={[
          "Score 0-100 con la lógica determinística del LLM",
          "Cada hallazgo cita la obligación legal exacta (artículo + literal)",
          "Recomendación accionable: qué cambiar y cómo",
          "Reporte descargable en PDF y compartible vía link público",
        ]}
        cta={{ to: "/", label: "Escanea ahora" }}
        mockup={<ScanMiniMockup />}
        flip={false}
      />

      {/* Pillar 02 — Generador Pro */}
      <Pillar
        num="02"
        category="Cumplimiento"
        title="Redacta los 7 documentos legales que la APDP fiscaliza."
        subtitle="El Módulo Pro toma tus respuestas de un onboarding de 7 minutos y genera los documentos alineados con la Guía oficial del Min. Segpres (dic 2025). Política con los 12 ítems exactos, Catálogo de Datos (RAT), DPA, CCM para transferencias, y más."
        bullets={[
          "7 documentos: Política Art. 14 ter, RAT, DPA, CCM, Cookies, Consent, Brechas",
          "Cláusulas Contractuales Modelo (RAEX 2025-03731 Min. Economía)",
          "PDF profesional con auditoría LLM incluida",
          "Re-genera si cambia tu negocio · revisiones ilimitadas",
        ]}
        badge="Pro · $14.990"
        cta={{ to: "/modulo-pro", label: "Ver Módulo Pro" }}
        mockup={<GeneratorMiniMockup />}
        flip={true}
      />

      {/* Pillar 03 — Deep Scan */}
      <Pillar
        num="03"
        category="Auditoría profunda"
        title="Audita 5 páginas internas. Detecta lo que se carga en checkout."
        subtitle="El Deep Scan auto-descubre y analiza las 5 páginas más críticas de tu sitio (home, política, cookies, contacto, checkout). Detecta pixels que solo cargan en páginas específicas y entrega recomendaciones por cada URL con cita legal."
        bullets={[
          "Auto-descubrimiento (sitemap.xml + probes a paths comunes)",
          "Trackers consolidados de todas las páginas",
          "Hallazgos contextualizados por URL con artículo y acción",
          "Persistencia en historial · re-scan ilimitado",
        ]}
        badge="Pro exclusivo"
        cta={{ to: "/modulo-pro", label: "Activar Deep Scan" }}
        mockup={<DeepScanMiniMockup />}
        flip={false}
      />
    </section>
  );
}

function Pillar({ num, category, title, subtitle, bullets, cta, mockup, badge, flip }) {
  return (
    <div className="px-6 py-20 sm:py-28 border-b border-slate-100">
      <div className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center ${flip ? "lg:[&>div:first-child]:order-2" : ""}`}>
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-display text-6xl text-blue-700 leading-none">{num}</span>
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] tracking-[0.22em] text-slate-500 uppercase">
                {category}
              </span>
              {badge && (
                <span className="inline-flex items-center w-fit text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                  {badge}
                </span>
              )}
            </div>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl text-slate-900 tracking-tight leading-tight">
            {title}
          </h3>
          <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-xl">
            {subtitle}
          </p>
          <ul className="mt-6 space-y-3 max-w-xl">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                <span className="shrink-0 w-4 h-px bg-slate-300 mt-2.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <Link
            to={cta.to}
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900 group"
          >
            {cta.label}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
        <div className="lg:col-span-6">{mockup}</div>
      </div>
    </div>
  );
}

/* ---------- Mockups por pilar ---------- */

function ScanMiniMockup() {
  return (
    <div className="bg-ink-950 text-white rounded-2xl shadow-xl border border-ink-800 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
        <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">Reporte · scan</p>
        <span className="font-mono text-[9px] text-emerald-400 inline-flex items-center gap-1.5">
          <span className="live-dot" /> Completo
        </span>
      </div>
      <div className="p-5">
        <p className="font-mono text-[10px] text-blue-300 uppercase tracking-widest mb-2">tu-empresa.cl</p>
        <p className="font-display text-5xl text-white leading-none">87<span className="text-base text-slate-500"> / 100</span></p>
        <div className="mt-4 h-1.5 bg-white/5 rounded-full">
          <div className="h-full bg-gradient-to-r from-blue-400 to-emerald-400 rounded-full" style={{ width: "87%" }} />
        </div>
        <div className="mt-5 space-y-2.5">
          <Hallazgo riesgo="alto" titulo="Tracker en checkout sin consentimiento" art="Art. 12 Ley 21.719" />
          <Hallazgo riesgo="medio" titulo="Política no incluye sección de decisiones automatizadas" art="Art. 14 ter literal 12" />
          <Hallazgo riesgo="bajo" titulo="Catálogo de Datos interno no publicado" art="Art. 16" />
        </div>
      </div>
    </div>
  );
}

function Hallazgo({ riesgo, titulo, art }) {
  const map = {
    alto: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    medio: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    bajo: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  };
  return (
    <div className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/5">
      <span className={`shrink-0 inline-flex text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[riesgo]}`}>
        {riesgo}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-white leading-snug">{titulo}</p>
        <p className="font-mono text-[10px] text-slate-500 mt-0.5">{art}</p>
      </div>
    </div>
  );
}

function GeneratorMiniMockup() {
  const docs = [
    { icon: FileText, name: "Política de Privacidad (12 ítems Art. 14 ter)", status: "Generado", color: "emerald" },
    { icon: ShieldCheck, name: "Catálogo de Datos Personales (RAT)", status: "Listo", color: "emerald" },
    { icon: FileText, name: "Cláusulas Contractuales Modelo", status: "Listo", color: "emerald" },
    { icon: Mail, name: "Cláusula consentimiento email", status: "En revisión", color: "amber" },
    { icon: ShoppingCart, name: "Cláusula decisiones automatizadas", status: "Pendiente", color: "slate" },
  ];
  const sMap = {
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    slate: "text-slate-400 bg-slate-500/10 border-slate-500/30",
  };
  return (
    <div className="bg-ink-950 text-white rounded-2xl shadow-xl border border-ink-800 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
        <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">Generador · documentos</p>
        <span className="font-mono text-[9px] text-blue-300">7 disponibles</span>
      </div>
      <div className="p-5 space-y-2.5">
        {docs.map((d, i) => {
          const Icon = d.icon;
          return (
            <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/5">
              <Icon className="w-3.5 h-3.5 text-blue-300 shrink-0" />
              <p className="flex-1 text-xs text-slate-200 truncate">{d.name}</p>
              <span className={`shrink-0 text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${sMap[d.color]}`}>
                {d.status}
              </span>
            </div>
          );
        })}
      </div>
      <div className="border-t border-white/5 px-5 py-3 flex items-center justify-between font-mono text-[10px] text-slate-500">
        <span>$14.990 CLP · pago único · permanente</span>
        <span className="inline-flex items-center gap-1.5 text-emerald-400">
          <CheckCircle2 className="w-3 h-3" /> Listo para PDF
        </span>
      </div>
    </div>
  );
}

function DeepScanMiniMockup() {
  const pages = [
    { type: "Home", url: "/", trackers: 2, forms: 0 },
    { type: "Política privacidad", url: "/politica-privacidad", trackers: 0, forms: 0 },
    { type: "Cookies", url: "/cookies", trackers: 0, forms: 0 },
    { type: "Contacto", url: "/contacto", trackers: 1, forms: 1, alert: true },
    { type: "Checkout", url: "/checkout", trackers: 4, forms: 2, alert: true },
  ];
  return (
    <div className="bg-ink-950 text-white rounded-2xl shadow-xl border border-ink-800 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
        <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">Deep Scan · multi-página</p>
        <span className="font-mono text-[9px] text-amber-300 inline-flex items-center gap-1.5">
          <Search className="w-3 h-3" /> 5 páginas
        </span>
      </div>
      <div className="p-5 space-y-2">
        {pages.map((p, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${p.alert ? "border-amber-500/20 bg-amber-500/5" : "border-white/5 bg-white/[0.02]"}`}
          >
            <span className="font-mono text-[9px] text-slate-500 uppercase tracking-wider shrink-0 w-3">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white">{p.type}</p>
              <p className="font-mono text-[10px] text-slate-500">{p.url}</p>
            </div>
            {p.trackers > 0 && (
              <span className="text-[9px] font-mono text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
                {p.trackers} trackers
              </span>
            )}
            {p.forms > 0 && (
              <span className="text-[9px] font-mono text-blue-300 bg-blue-500/10 border border-blue-500/30 px-1.5 py-0.5 rounded">
                {p.forms} form
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-white/5 px-5 py-3 flex items-center justify-between font-mono text-[10px] text-slate-500">
        <span>7 trackers únicos · 3 formularios</span>
        <span className="text-rose-300 inline-flex items-center gap-1.5">
          <ShoppingCart className="w-3 h-3" /> Riesgo en checkout
        </span>
      </div>
    </div>
  );
}
